// Replaces the entire list pane (header, rows, footer) while a look-back is
// open (docs/06-ux-rules.md "Look-back, R3"; 11-chassis-plan.md §2 PR 04).
// Focuses itself on mount so keyboard users land somewhere sensible when
// the pane's contents swap out from under them.
import { Button } from '@thanx/uikit/button'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef } from 'react'
import { useStyletron } from 'styletron-react'
import { formatDate } from 'utilities/dateFormatters'
import { textLinkButtonOverrides } from '../textLinkButtonOverrides'

const t = buildTranslate('feedback_inbox.lookback')

export interface LookbackBackHeaderProps {
  onBack: () => void
  // ISO date of the past order being viewed. Copy is omitted until known.
  date?: string | null
}

const LookbackBackHeader = ({ onBack, date }: LookbackBackHeaderProps) => {
  const [css] = useStyletron()
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    buttonRef.current?.focus()
  }, [])

  return (
    <div className={css({ padding: '16px' })}>
      <Button
        ref={buttonRef}
        kind="minimal"
        onClick={onBack}
        aria-label={t('back_to_feedback_aria')}
        // `textLinkButtonOverrides` rather than a local override: a
        // hand-rolled one here omitted `:hover { backgroundColor }` and
        // showed baseui's near-black `buttonPrimaryHover` (#002E4C) behind
        // the label on hover. See that helper's header comment for why the
        // hover/focus/disabled states all have to be re-declared. The
        // `extra` args keep this button's own larger, pane-level typography
        // (16px/700) instead of the helper's inline-text-link default.
        overrides={textLinkButtonOverrides({
          paddingTop: '4px',
          paddingBottom: '4px',
          fontSize: '16px',
          fontWeight: 700,
        })}
      >
        <i
          className={`fa fa-arrow-left ${css({ marginRight: '8px' })}`}
          aria-hidden="true"
        />
        {t('back_to_feedback')}
      </Button>
      {date && (
        <p
          className={css({
            marginTop: '12px',
            marginBottom: 0,
            fontSize: '16px',
            lineHeight: '24px',
            color: palette.grey70,
          })}
          // The locale string darkens "past order" and the date with
          // <strong class="grey-90">; styletron can't target nested tags.
          dangerouslySetInnerHTML={{
            __html: t('banner', { date: formatDate(date) }),
          }}
        />
      )}
    </div>
  )
}

export default LookbackBackHeader
